import { BuilderService } from 'src/app/builder-services/builder.service'
import { Component, OnInit } from '@angular/core'
import { ProductsService } from 'src/app/e-commerce/products/products.service'
import { ViewsService } from 'src/app/views/views.service'
import { PagesService } from 'src/app/pages/website-builder/pages.service'
import { FormBuilder, FormGroup } from '@angular/forms'
import { HttpClient } from '@angular/common/http'
import { MatTableDataSource } from '@angular/material/table'
import { SettingsService } from 'src/app/e-commerce/settings/settings.service'
import { forkJoin, from, Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { ShippingService } from 'src/app/e-commerce/shipping/shipping.service'
import { CategoriesService } from 'src/app/e-commerce/categories/categories.service'
import { TranslationService } from '../translation.service'
import { HelpersService } from 'src/app/helpers.service'
import { Location } from '@angular/common'
import { environment } from 'src/environments/environment'

interface TableRow {
  [key: string]: any
  Currency?: string
  Key: string
}

@Component({
  selector: 'app-txt-translations',
  templateUrl: './txt-translations.component.html',
  styleUrl: './txt-translations.component.scss'
})
export class TxtTranslationsComponent implements OnInit {
  currentPageIndex: number = 0
  displayedColumns: string[] = []
  editingCell: { row: TableRow | null; lang: string | null } = {
    row: null,
    lang: null
  }
  loader: boolean = false
  filteredData: TableRow[] = []
  languages: { title: string; translation?: string }[] = []
  pagedTableData: MatTableDataSource<TableRow> = new MatTableDataSource<TableRow>([])
  pageSize: number = 10
  pageSizeOptions: number[] = [5, 10, 25, 50, 100]
  searchText: string = ''
  tableData: TableRow[] = []
  txtTranslations!: FormGroup
  project_id: string | undefined
  translateNumbers: boolean = true
  defaultLanguage: string = ''
  private translationsCache: Record<string, Record<string, string>> = {}
  constructor(
    private BuilderService: BuilderService,
    private productsService: ProductsService,
    private viewsService: ViewsService,
    private pagesService: PagesService,
    private http: HttpClient,
    private settingsService: SettingsService,
    private translationService: TranslationService,
    private categoriesService: CategoriesService,
    private helpers: HelpersService,
    private location: Location,
    private shippingService: ShippingService,
    private fb: FormBuilder
  ) {
    this.project_id = this.BuilderService.selectedProject
  }

  /*
   * ngOnInit: Initializes the component.
   * - Sets up the form group for translations.
   * - Initializes an array for languages.
   * - Loads available languages and retrieves currency data.
   */

  ngOnInit(): void {
    this.txtTranslations = this.fb.group({
      languages: this.fb.array([])
    })
    this.loadLanguages()
  }

  navigateBack(): void {
    this.location.back();
  }

  /*
   * updatePageData: Updates the displayed data in the paginated table.
   * - Takes a pageIndex as a parameter to determine which data to display.
   * - Calculates start and end indices based on the current page index and page size.
   * - Slices the filtered data to set the data for the paginated table.
   */

  updatePageData(pageIndex: number): void {
    const startIndex = pageIndex * this.pageSize
    const endIndex = startIndex + this.pageSize
    this.pagedTableData.data = this.filteredData.slice(startIndex, endIndex)
  }

  /*
   * onPageChange: Handles the page change event for the paginated table.
   * - Updates the page size and current page index based on the event.
   * - Calls updatePageData to refresh the displayed data for the current page.
   */

  onPageChange(event: any) {
    this.pageSize = event.pageSize
    this.currentPageIndex = event.pageIndex
    this.updatePageData(this.currentPageIndex)
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredData.length / this.pageSize)
  }

  goToPreviousPage(): void {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--
      this.updatePageData(this.currentPageIndex)
    }
  }

  goToNextPage(): void {
    if (this.currentPageIndex < this.getTotalPages() - 1) {
      this.currentPageIndex++
      this.updatePageData(this.currentPageIndex)
    }
  }
  /*
   * getTranslate: Fetches translations for the selected project and aggregates
   * the data from multiple languages.
   * - Retrieves the selected project ID from the BuilderService.
   * - Checks if any languages are available; logs a message and exits if none.
   * - Initializes a counter and an object to hold aggregated translation data.
   * - Iterates over each language to construct the unique database path for translations.
   * - Subscribes to the database observable to fetch translations for each language.
   * - Aggregates translations into the allData object, using the 'En' key as a reference.
   * - When all language data is loaded, converts the aggregated data to an array,
   *   adds a currency row, filters the data, and updates the displayed page data.
   * - Logs an error if the fetched data is not in the expected format or if the fetch fails.
   */

  async getTranslate(): Promise<void> {
    if (!this.languages.length) {
      console.log('No languages available.')
      return
    }

    const allData: { [key: string]: any } = {}
    this.translationsCache = {}

    for (const lang of this.languages) {
      const translationsData = await this.translationService.getAllTranslationsForLanguage(lang.title)
      this.translationsCache[lang.title] = {}
      for (const key in translationsData) {
        if (!allData[key]) allData[key] = { Key: key }
        allData[key][lang.title] = translationsData[key].value
        this.translationsCache[lang.title][key] = translationsData[key].value
      }
    }

    this.tableData = Object.values(allData)
    this.fetchAndAddTextRows()
    this.fetchAndAddWebsitePageTextRows()
    this.getCurrency().subscribe(() => { this.addCurrencyRow() })
    this.filterData()
    this.updatePageData(this.currentPageIndex)
  }

  /**
   * Loads data from multiple sources including products, menu items, section texts, button texts,
   * product tags, and country names. Integrates the fetched data into the `tableData` structure
   * and ensures duplicates are removed. Handles asynchronous fetching of:
   * - Product variation values and attribute names
   * - Product tag categories (e.g., "brands", "colors") and individual tag values (e.g., "adidas", "red")
   * - Menu items, section texts, and button texts
   * - Country and governorate names
   * - Translation data from a static JSON file
   *
   * Ensures the `tableData` is updated with the latest combined information, with each unique
   * text value added as a separate row for translation.
   */
  loadData(): void {
        forkJoin({
          products: from(this.productsService.getAllProducts()),
          categories: this.getCategoryNames(),
          countries: this.getCountryAndGovernorateNames(),
          translations: this.http.get<Record<string, string>>(`${environment.agentsUrl}v4/translations/arabic-strings`)
        }).subscribe(
          ({ products, categories, countries, translations }: any) => {
            const { productVariationValues, productAttributeNames, productDescription, productNames, tagCategories, tagValues, productBadges } = this.translationService.extractProductData(products)
            const menuItems = this.translationService.getMenuItems()
            const { countryNames, governorateNames, areaNames, cityNames } = countries
            const englishTexts = Object.keys(translations)

            this.integrateDataIntoTable({
              translationData: englishTexts,
              menuItems,
              productVariationValues,
              productAttributeNames,
              tagCategories,
              tagValues,
              countryNames,
              governorateNames,
              cityNames,
              areaNames,
              productNames,
              productDescription,
              productBadges,
              categoryNames: categories
            })
            this.tableData.forEach((row) => {
              const key = row.Key?.trim()
              if (key && translations[key] && !row['Arabic']) {
                row['Arabic'] = translations[key]
              }
            })

            this.postProcessUpdates()
          },
          (error: any) => {
            console.error('Error fetching translation data:', error)
      }
    )
  }
  /**
   * Fetches category names from the CategoriesService.
   * @returns An Observable of category names as an array of strings.
   */
  private getCategoryNames(): Observable<string[]> {
    return this.categoriesService.getCategories().pipe(
      map((response) => {
        if (response && Array.isArray(response)) {
          return response.map((category: any) => category.categoryName).filter((name: string) => name && typeof name === 'string' && name.trim().length > 0)
        }
        return []
      })
    )
  }

  /*
   * integrateDataIntoTable: Aggregates multiple sources of translatable text,
   * deduplicates them, and inserts them into the table with language detection.
   *
   * Workflow:
   * 1. Merge all provided arrays (translationData, menuItems, buttonTexts, etc.)
   *    into a single `allData` list.
   * 2. Pass the combined list to `processTextsWithDetection`, which:
   *      - Deduplicates the texts.
   *      - Detects their language.
   *      - Creates/updates table rows for each text.
   *      - Optionally hydrates existing translations from the database
   *        when `fillExisting: true` is set.
   *
   * This ensures all UI, product, and category-related strings are unified
   * in `tableData` for translation management.
   */
  private integrateDataIntoTable({
    translationData,
    menuItems,
    productVariationValues,
    productAttributeNames,
    tagCategories,
    tagValues,
    countryNames,
    governorateNames,
    cityNames,
    areaNames,
    productNames,
    productDescription,
    productBadges,
    categoryNames
  }: {
    translationData: string[]
    menuItems: string[]
    productVariationValues: string[]
    productAttributeNames: string[]
    tagCategories: string[]
    tagValues: string[]
    countryNames: string[]
    governorateNames: string[]
    cityNames: string[]
    areaNames: string[]
    productNames: string[]
    productDescription: string[]
    productBadges: string[]
    categoryNames: string[]
  }): void {
    const allData = [...translationData, ...menuItems, ...productVariationValues, ...productAttributeNames, ...tagCategories, ...tagValues, ...countryNames, ...governorateNames, ...cityNames, ...areaNames, ...productNames, ...productDescription, ...productBadges, ...categoryNames].filter((text) => text && text.trim().length > 0)

    // detect → add to table; skip DB fetch here if you want it faster
    this.processTextsWithDetection(allData, { fillExisting: true })
  }

  /**
   * Retrieves a list of country names and their governorate names by fetching data from the shipping service.
   */
  private getCountryAndGovernorateNames(): Observable<{
    countryNames: string[]
    governorateNames: string[]
    cityNames: string[]
    areaNames: string[]
  }> {
    return this.shippingService.getCountries().pipe(
      map((countries: any[]) => {
        if (!countries || !countries.length) {
          return { countryNames: [], governorateNames: [], cityNames: [], areaNames: [] }
        }
        const countryNames: string[] = countries.map((country) => country.countryName)
        const governorateNames: string[] = []
        const cityNames: string[] = []
        const areaNames: string[] = []

        countries.forEach((country) => {
          if (country.governorates && Array.isArray(country.governorates)) {
            country.governorates.forEach((governorate: any) => {
              governorateNames.push(governorate.governorateName)
              if (Array.isArray(governorate.areas)) {
                governorate.areas.forEach((area: any) => {
                  areaNames.push(area.areaName)
                })
              }
              // ✅ Collect cities if exist
              if (Array.isArray(governorate.cities)) {
                governorate.cities.forEach((city: any) => {
                  cityNames.push(city.cityName)
                  // Collect areas inside each city
                  if (Array.isArray(city.areas)) {
                    city.areas.forEach((area: any) => {
                      areaNames.push(area.areaName)
                    })
                  }
                })
              }
            })
          }
        })
        return { countryNames, governorateNames, areaNames, cityNames }
      })
    )
  }

  /**
   * postProcessUpdates: Performs necessary post-processing steps after `this.tableData` has been updated.
   * - Fetches and adds additional text rows by calling `fetchAndAddTextRows()`.
   * - Incorporates currency-specific data into the table by invoking `addCurrencyRow()`.
   * - Applies any active filters to the updated data using `filterData()`.
   * - Refreshes the displayed data for the current page index with `updatePageData()`.
   */

  private postProcessUpdates(): void {
    this.fetchAndAddTextRows()
    this.fetchAndAddWebsitePageTextRows()
    this.addCurrencyRow()
    this.filterData(true)
  }

  /*
   * toggleEdit: Toggles the edit mode for a specific cell in the table.
   * - Accepts a row of type TableRow and a language string as parameters.
   * - If the specified row and language are already being edited, it resets the editingCell to null.
   * - Otherwise, it sets the editingCell to the provided row and language, enabling editing for that cell.
   */

  toggleEdit(row: TableRow, lang: string): void {
    if (this.editingCell.row === row && this.editingCell.lang === lang) {
      this.editingCell = { row: null, lang: null }
    } else {
      this.editingCell = { row, lang }
    }
  }

  /*
   * filterData: Applies a search filter across all table rows and updates the displayed results.
   *
   * Workflow:
   * 1. If `searchText` is provided:
   *    - Normalize it to lowercase (`searchLower`) for case-insensitive matching.
   *    - Iterate through `tableData` rows and check:
   *        • `Key` field (main text).
   *        • `Currency` field (if present).
   *        • All language-specific fields in `this.languages`.
   *      Include the row in `filteredData` if any of these contain the search string.
   * 2. If no `searchText` is provided:
   *    - Copy all rows from `tableData` into `filteredData`.
   * 3. Call `updatePageData(0)` to reset pagination and display results starting from page 1.
   */

  filterData(resetPage = false): void {
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase()
      this.filteredData = this.tableData.filter((row) => {
        const keyValue = row.Key ? String(row.Key).toLowerCase() : ''
        const matchKey = keyValue.includes(searchLower)
        const currencyValue = row.Currency ? String(row.Currency).toLowerCase() : ''
        const matchCurrency = currencyValue.includes(searchLower)

        // Check across your languages array:
        const matchLang = (this.languages || []).some((lang) => {
          const langValue = String(row[lang.title] || '').toLowerCase()
          return langValue.includes(searchLower)
        })

        return matchKey || matchCurrency || matchLang
      })
    } else {
      // Copy all data if no search text
      this.filteredData = [...this.tableData]
    }
    if (resetPage) {
      this.currentPageIndex = 0
    }
    this.updatePageData(this.currentPageIndex)
  }

  /*
   * loadLanguages: Fetches and initializes the list of supported languages for the active project.
   *
   * Workflow:
   * 1. Build the database path: `/projects/{project_id}/translations/languages`.
   * 2. Query the database for available language data.
   * 3. If the response is an object:
   *    - Convert it into an array of language objects:
   *        • Each entry → { title: <language name>, translation: <language data> }.
   *    - Update `this.languages` with the array.
   *    - Mirror these languages into `displayedColumns` for the table.
   *    - Call `getTranslate()` to populate translations for the loaded languages.
   * 4. If data format is unexpected, log an error.
   * 5. If the database request fails, log the error.
   */

  async loadLanguages(): Promise<void> {
    const config = await this.translationService.getTranslationConfig()
    this.translateNumbers = config?.translateNumbers ?? true
    this.defaultLanguage = config?.defaultLanguage ?? 'English'

    const langs = await this.translationService.getProjectLanguages()
    if (!langs.length) {
      console.error('No languages found for project')
      return
    }

    this.languages = langs.map((lang) => ({ title: lang, translation: undefined }))
    this.languages.sort((a, b) => {
      if (a.title === this.defaultLanguage) return -1
      if (b.title === this.defaultLanguage) return 1
      return 0
    })
    this.displayedColumns = [...this.languages.map((lang) => lang.title)]
    this.getTranslate()
  }

  /*
   * saveChanges: Persists all translations in `tableData` to the database for every language.
   *
   * Workflow:
   * 1. Enable the loading indicator (`translatePipe.loader = true`) to show save in progress.
   * 2. Ensure each row in `tableData` has a value (string, even empty) for every language column.
   * 3. For each language:
   *    - Build a `translationsObject` where:
   *        • Key = sanitized version of the source string (`row.Key`).
   *        • Value = `{ value: <translation text for that language> }`.
   *    - Construct the database path:
   *        `/projects/{project_id}/translations/languages/{languageTitle}`.
   *    - Save the entire `translationsObject` to that path via `setDatabase`.
   * 4. On success: mark loading as false.
   * 5. On error: log the failure, mark loading as false, and continue with other languages.
   */

  async saveChanges(): Promise<void> {
    this.loader = true

    this.tableData.forEach((row) => {
      this.languages.forEach((lang) => {
        row[lang.title] = row[lang.title] || ''
      })
    })

    for (const lang of this.languages) {
      const translations: Record<string, string> = {}

      this.tableData.forEach((row) => {
        const key = this.helpers.generateValidKey(row.Key)
        if (!key) return
        if (!this.helpers.canBeSavedToFirebase(key)) return
        if (key.length > 500) return
        translations[key] = row[lang.title] || ''
      })

      try {
        await this.translationService.upsertTranslations(lang.title, translations)
        if (!this.translationsCache[lang.title]) this.translationsCache[lang.title] = {}
        Object.assign(this.translationsCache[lang.title], translations)
      } catch (error) {
        console.error(`Failed to save translations for ${lang.title}:`, error)
      }
    }

    this.loader = false
  }

  /*
   * getCurrency: Fetches the currency settings for the application.
   * - Subscribes to the settingsService to retrieve currency data.
   * - On successful retrieval, assigns the currency data to the currency property
   *   and calls addCurrencyRow() to incorporate the currency into the table.
   * - Logs an error message if fetching the currency data fails.
   */

  getCurrency(): Observable<any> {
    return new Observable((observer) => {
      this.settingsService.getCurrencySettings().subscribe(
        (data) => {
          if (data) {
            observer.next(data)
            observer.complete()
          } else {
            console.warn('Currency data is empty or invalid')
            observer.error('Currency data is empty')
          }
        },
        (error) => {
          console.error('Error fetching currency data:', error)
          observer.error(error)
        }
      )
    })
  }

  /*
   * addCurrencyRow: Adds a new row for the currency to the table data.
   * - Checks if currency data is available; if so, creates a new currencyRow
   *   object with the currency symbol.
   * - For each language, constructs a unique database path to fetch the currency
   *   translation.
   * - Subscribes to the database to retrieve the currency translation for each language,
   *   adding it to the currencyRow.
   * - If the currencyRow does not already exist in the tableData, it adds it.
   * - Calls filterData() to apply any active search filters after updating the table.
   * - Logs an error if fetching the currency translation fails for any language.
   */
  addCurrencyRow() {
    const currency = this.settingsService.currency
    if (!currency || typeof currency === 'object') return

    const text = String(currency).trim()
    if (!text) return

    // detect → add to table → (optionally) hydrate from Firebase
    this.processTextsWithDetection([text], { fillExisting: true })
  }

  /*
   * addTextRows: Extracts text blocks, detects their language, and integrates them into the table.
   *
   * Workflow:
   * 1. Traverse all `textBlocks` and collect text values from blocks of type 'text'.
   *    - Extracts raw text (via `extractTextFromHTML`).
   *    - Skips empty values, trims whitespace.
   * 2. Deduplicate collected texts to avoid duplicate rows.
   * 3. Pass the unique texts to `processTextsWithDetection`:
   *    - Detects the language of each text in batches (Google Translate API).
   *    - Ensures a row exists in `tableData` for each text.
   *    - Places the text under its detected language column.
   *    - Optionally hydrates existing translations from Firebase (if `fillExisting: true`).
   * 4. Calls `filterData()` internally (via `processTextsWithDetection`) to re-apply active filters
   *    and update the displayed table data.
   */

  addTextRows(textBlocks: any, blocksData?: any[]): void {
    // allow mixed values; we’ll normalize/flatten below
    const collected: any[] = this.translationService.extractBlockTexts(textBlocks) as any[]

    if (Array.isArray(blocksData)) {
      const btn = this.translationService.extractButtonTexts?.(blocksData) || []
      const sec = this.translationService.extractSectionTexts?.(blocksData) || []
      const header = this.translationService.extractHeaderTexts?.(blocksData) || []
      const footer = this.translationService.extractFooterTexts?.(blocksData) || []
      // these might already be arrays; push as-is and normalize later
      collected.push(...btn, ...sec, ...header, ...footer)
    }

    // ✅ flatten nested arrays, trim strings, drop empties, then de-dup (preserving order)
    const normalized = this.helpers.flattenAndTrimToStrings(collected)
    const unique = this.translationService.removeDuplicatesPreserveOrder(normalized)
    if (!unique.length) return

    this.processTextsWithDetection(unique, { fillExisting: true })
  }

  /*
   * fetchAndAddTextRows: Fetches text blocks from the database and processes them.
   * - Constructs the database path based on the selected project.
   * - Subscribes to the database to retrieve text blocks.
   * - If text blocks are found, passes them to the `addTextRows` function for processing and translation.
   * - Logs a message if no text blocks are found.
   */

  async fetchAndAddTextRows(): Promise<void> {
    const viewsData = await this.viewsService.getProjectViews(this.project_id!)
    if (!viewsData || !Object.keys(viewsData).length) {
      console.log('No text blocks found.')
      return
    }
    const textBlocks: any = {}
    const allBlocks: any[] = []
    for (const [viewId, view] of Object.entries(viewsData as Record<string, any>)) {
      const blocks = Array.isArray(view.allBlocks) ? view.allBlocks : []
      if (blocks.length) {
        textBlocks[viewId] = { allBlocks: blocks }
        allBlocks.push(...blocks)
      }
    }
    if (allBlocks.length) this.addTextRows(textBlocks, allBlocks)
  }

  /**
   * Fetches text from website builder pages and processes them for translation.
   */
    async fetchAndAddWebsitePageTextRows(): Promise<void> {
      const [pagesData, headerData, footerData] = await Promise.all([
        this.pagesService.getProjectPages(this.project_id!),
        this.pagesService.getProjectPageStatic(this.project_id!, 'header'),
        this.pagesService.getProjectPageStatic(this.project_id!, 'footer'),
      ])

      const allBlocks: any[] = []
      const textBlocks: any = {}

    if (pagesData && Object.keys(pagesData).length) {
      Object.keys(pagesData).forEach((pageKey) => {
        const page = (pagesData as Record<string, any>)[pageKey]

        let blocks: any[] = []
        if (page.all_blocks) {
          try {
            blocks = typeof page.all_blocks === 'string' ? JSON.parse(page.all_blocks) : page.all_blocks
          } catch (e) {
            console.warn(`Failed to parse all_blocks for page ${pageKey}:`, e)
          }
        } else if (page.allBlocks) {
          blocks = Array.isArray(page.allBlocks) ? page.allBlocks : []
        }

        if (blocks.length) {
          textBlocks[pageKey] = { allBlocks: blocks }
          allBlocks.push(...blocks)
        }
      })
    }

      if (headerData) {
        const headerBlocks = Array.isArray(headerData) ? headerData : [headerData]
        textBlocks['header'] = { allBlocks: headerBlocks }
        allBlocks.push(...headerBlocks)
      }
      if (footerData) {
        const footerBlocks = Array.isArray(footerData) ? footerData : [footerData]
        textBlocks['footer'] = { allBlocks: footerBlocks }
        allBlocks.push(...footerBlocks)
      }

      if (allBlocks.length) this.addTextRows(textBlocks, allBlocks)
    }

  private mapDetectedLang(code: string): string {
    // Normalize
    const lower = code.toLowerCase()

    // Try to find a match in your languages array
    const match = this.languages.find((l) => l.title.toLowerCase() === lower || l.title.toLowerCase().startsWith(lower))
    if (match) return match.title

    // Otherwise fallback to the raw code
    return code
  }
  /**
   * Splits a large list of texts into smaller batches (default 25 each)
   * and calls `detectLanguageFromText` for each batch.
   * Returns an array of observables that emit detected language codes.
   */

  private buildDetectionBatches(items: string[], max: number): Observable<string[]>[] {
    const batches: Observable<string[]>[] = []
    for (let i = 0; i < items.length; i += max) {
      const slice = items.slice(i, i + max)
      const detectedLangs = slice.map((text) => this.translationService.detectLanguageFromText(text))
      const obs = of(detectedLangs)
      batches.push(obs)
    }
    return batches
  }
  /**
   * Finds or creates a table row for a given text.
   * If the row doesn’t exist, initializes it with empty language columns.
   */
  private ensureRowForText(text: string): TableRow {
    let row = this.tableData.find((r) => r.Key === text)
    if (!row) {
      const newRow: TableRow = { Key: text } as TableRow
      // init all language columns empty (or 'Not Translated' if you prefer)
      this.languages.forEach((l: any) => {
        ;(newRow as any)[l.title] = ''
      })
      this.tableData.push(newRow)
      row = newRow
    }
    return row
  }
  /**
   * Loads any existing translations from the database for a given text and language.
   * Populates the row’s columns if values are found.
   */
  private fillExistingTranslations(rowRef: TableRow, text: string): void {
    const sanitizedKey = this.helpers.generateValidKey(text)
    if (sanitizedKey.length > 500) return
    for (const l of this.languages) {
      const cached = this.translationsCache[l.title]?.[sanitizedKey]
      if (cached && !(rowRef as any)[l.title]) {
        ;(rowRef as any)[l.title] = cached
      }
    }
  }

  /**
   * Core processor:
   * 1. Deduplicates & trims input texts.
   * 2. Detects their languages in batches.
   * 3. Ensures each text has a row, places it under the detected language column,
   *    and optionally hydrates existing translations from the database.
   * 4. Updates the table view.
   */
  private processTextsWithDetection(texts: string[], opts: { fillExisting?: boolean } = {}): void {
    const { fillExisting = true } = opts

    const uniqueData = Array.from(new Set(texts.filter(Boolean).map((t) => t.trim())))
    if (uniqueData.length === 0) return

    const batches = this.buildDetectionBatches(uniqueData, 5)

    forkJoin(batches).subscribe((batchesOut: string[][]) => {
      const detectedAll: string[] = ([] as string[]).concat(...batchesOut)

      for (let i = 0; i < uniqueData.length; i++) {
        const text = uniqueData[i]
        let detected = (i < detectedAll.length ? detectedAll[i] : '') || ''

        if (typeof this.mapDetectedLang === 'function') {
          detected = this.mapDetectedLang(detected) // map code → UI title
        }

        // Try to find detected language in loaded languages
        let langObj = this.languages.find((l: any) => l.title?.toLowerCase() === detected.toLowerCase())

        // ✅ Always fallback to English or Arabic if nothing detected
        if (!langObj) {
          langObj = this.languages.find((l: any) => l.title.toLowerCase() === 'english') || this.languages.find((l: any) => l.title.toLowerCase() === 'arabic')
        }
        // Always ensure the row exists (so text isn’t lost)
        const rowRef = this.ensureRowForText(text)

        if (langObj) {
          // Ensure column exists
          if (!this.displayedColumns.includes(langObj.title)) {
            this.displayedColumns = [...this.displayedColumns, langObj.title]
          }

          // ensure row, then write text under detected lang
          ;(rowRef as any)[langObj.title] = text
        }

        // optionally hydrate existing translations from DB
        if (fillExisting) this.fillExistingTranslations(rowRef, text)
      }

      this.filterData()
      this.updatePageData?.(this.currentPageIndex)
    })
  }

  exportTranslationData(): void {
    if (!this.tableData || !this.tableData.length) return

    const headers = ['Key', ...this.languages.map((l) => l.title)]
    const csvRows = [headers.join(',')]

    this.tableData.forEach((row) => {
      const rowData = [
        `"${(row.Key || '').replace(/"/g, '""')}"`, // include Key
        ...this.languages.map((l) => `"${(row[l.title] || '').replace(/"/g, '""')}"`)
      ]
      csvRows.push(rowData.join(','))
    })

    const csvString = csvRows.join('\n')
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = `translations_${this.project_id}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  importTranslationData(event: any): void {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      let csvText = reader.result as string

      // Remove UTF-8 BOM if present
      csvText = csvText.replace(/^\uFEFF/, '')

      // Split rows safely (handle Windows/Mac line endings)
      const rows = csvText.split(/\r?\n/).filter((r) => r.trim().length > 0)

      // Parse headers safely
      const headers = this.parseCsvRow(rows[0])
      const keyIndex = headers.indexOf('Key')
      if (keyIndex === -1) {
        console.error('CSV must include a Key column.')
        return
      }

      const languageHeaders = headers.filter((h) => h !== 'Key')

      // Parse data rows
      const dataRows = rows.slice(1).map((row) => {
        const values = this.parseCsvRow(row)
        const obj: any = {}
        headers.forEach((header, i) => {
          obj[header] = values[i] || ''
        })
        return obj
      })

      // Update tableData without saving yet
      this.tableData = dataRows.map((row) => {
        const newRow: TableRow = { Key: row['Key'] }
        this.languages.forEach((lang) => {
          newRow[lang.title] = row[lang.title] || ''
        })
        return newRow
      })
      this.filterData(true)
    }

    reader.readAsText(file)
  }

  /**
   * Safe CSV row parser
   * Handles commas, quotes, and escaped quotes inside cells.
   */
  private parseCsvRow(row: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < row.length; i++) {
      const char = row[i]
      const nextChar = row[i + 1]

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"' // escaped quote
        i++ // skip the next quote
      } else if (char === '"') {
        inQuotes = !inQuotes // toggle quote state
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }

    result.push(current.trim())
    return result
  }
}
